import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, ListingCardSkeleton, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

// Placeholder cards shown while a search runs: enough to fill the first rows.
const SKELETON_COUNT = 8;

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {boolean} [props.isLoading] - Show placeholder cards while a new search runs
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    isLoading = false,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 50vw',
        `(max-width: 1023px) ${panelMediumWidth / 2}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      return [
        '(max-width: 767px) 50vw',
        '(max-width: 1023px) 33vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul
        className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}
        aria-busy={isLoading || undefined}
      >
        {isLoading
          ? [...Array(SKELETON_COUNT).keys()].map(i => (
              <li key={`skeleton-${i}`} className={css.resultItem}>
                <ListingCardSkeleton />
              </li>
            ))
          : listings.map(l => (
              <li key={l.id.uuid} className={css.resultItem}>
                <ListingCard
                  className={css.listingCard}
                  listing={l}
                  renderSizes={cardRenderSizes(isMapVariant)}
                  setActiveListing={setActiveListing}
                />
              </li>
            ))}
        {props.children}
      </ul>
      {isLoading ? null : paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
